import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import KanbanCard from './KanbanCard';
import { Typography, IconButton, Tooltip } from '@material-ui/core';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import formatToCurrency from "../../utils/formatToCurrency";

const useStyles = makeStyles(theme => ({
  column: props => ({
    borderRadius: 12,
    minWidth: 280,
    maxWidth: 280,
    marginRight: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    scrollSnapAlign: 'start',
    background: theme.palette.type === 'dark'
      ? 'rgba(15,23,42,0.70)'
      : 'rgba(241,245,249,0.95)',
    border: '1px solid',
    borderColor: theme.palette.type === 'dark'
      ? 'rgba(255,255,255,0.06)'
      : 'rgba(0,0,0,0.07)',
    borderTopWidth: 3,
    borderTopColor: props.color || '#475569',
    boxShadow: theme.palette.type === 'dark'
      ? '0 4px 16px rgba(0,0,0,0.30)'
      : '0 2px 8px rgba(0,0,0,0.06)',
    overflow: 'hidden',
    [theme.breakpoints.down('xs')]: {
      minWidth: 'calc(100vw - 20px)',
      maxWidth: 'calc(100vw - 20px)',
      marginRight: theme.spacing(1),
    },
  }),
  columnHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 1, 0.5, 0.5),
    gap: 4,
  },
  dragHandle: {
    cursor: 'grab',
    color: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.20)',
    padding: 4,
    flexShrink: 0,
    '&:hover': {
      color: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.50)',
    },
  },
  colorDot: props => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: props.color || '#475569',
    flexShrink: 0,
  }),
  columnTitle: {
    fontWeight: 700,
    fontSize: 13,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: theme.palette.type === 'dark' ? '#e2e8f0' : '#1e293b',
    letterSpacing: '-0.01em',
  },
  countBadge: props => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 20,
    height: 20,
    padding: '0 6px',
    borderRadius: 10,
    background: props.color ? `${props.color}25` : 'rgba(91,45,142,0.12)',
    color: props.color || '#5B2D8E',
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
    flexShrink: 0,
  }),
  totalValue: {
    padding: theme.spacing(0, 1, 0.75, 1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  totalText: {
    fontSize: 11,
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? '#475569' : '#94a3b8',
  },
  cardList: {
    flexGrow: 1,
    overflowY: 'auto',
    padding: theme.spacing(0.5, 0.75, 0.75),
    maxHeight: 'calc(100vh - 230px)',
    [theme.breakpoints.down('xs')]: {
      maxHeight: 'calc(100vh - 200px)',
    },
    '&::-webkit-scrollbar': { width: 4 },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 4,
      background: 'rgba(128,128,128,0.20)',
    },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(3, 1),
    color: theme.palette.type === 'dark' ? '#334155' : '#cbd5e1',
    fontSize: 12,
    fontStyle: 'italic',
  },
}));

const KanbanColumn = ({ id, title, tickets, color, index, updateTicket, isAdmin }) => {
  const classes = useStyles({ color });

  const totalValue = tickets.reduce((acc, ticket) => {
    const customFields = ticket.contact.extraInfo || [];
    const valueField = customFields.find(field => field.name === 'valor');
    return acc + (valueField ? parseFloat(valueField.value) : 0);
  }, 0);

  return (
    <Draggable draggableId={id} index={index}>
      {(provided) => (
        <div
          className={classes.column}
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          {/* Column header */}
          <div className={classes.columnHeader}>
            {isAdmin && (
              <div {...provided.dragHandleProps} className={classes.dragHandle}>
                <DragIndicatorIcon style={{ fontSize: 16 }} />
              </div>
            )}
            <span className={classes.colorDot} />
            <Typography className={classes.columnTitle}>{title}</Typography>
            <Tooltip title={`${tickets.length} ticket${tickets.length !== 1 ? 's' : ''}`}>
              <span className={classes.countBadge}>{tickets.length}</span>
            </Tooltip>
          </div>

          {/* Total value */}
          {totalValue > 0 && (
            <div className={classes.totalValue}>
              <Typography className={classes.totalText}>
                Total: {formatToCurrency(totalValue)}
              </Typography>
            </div>
          )}

          {/* Cards */}
          <Droppable droppableId={id} type="CARD">
            {(provided, snapshot) => (
              <div
                className={classes.cardList}
                ref={provided.innerRef}
                {...provided.droppableProps}
                style={{
                  background: snapshot.isDraggingOver
                    ? `${color}10`
                    : undefined,
                  transition: 'background 0.15s',
                }}
              >
                {tickets.length === 0 && !snapshot.isDraggingOver && (
                  <Typography className={classes.emptyState}>
                    Sin tickets
                  </Typography>
                )}
                {tickets.map((ticket, idx) => (
                  <KanbanCard
                    key={ticket.id}
                    ticket={ticket}
                    index={idx}
                    updateTicket={updateTicket}
                  />
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      )}
    </Draggable>
  );
};

export default KanbanColumn;
